import { CompanyType } from '../../../companyService/company/types/enums';

import { UserRole } from './enums';

/**
 * Atomic role groups. Each is a single, indivisible access concept; every
 * composite below is built from these, so a role belongs to exactly one atomic
 * group and cannot drift between them.
 */
const ATOMIC_ROLES = {
  /** Platform operator. */
  ADMIN: [UserRole.SUPER_ADMIN],

  /** Certified inspectors. */
  INSPECTORS: [UserRole.INSPECTOR, UserRole.PROVEN_INSPECTOR],

  /** Certified inspectors only, excluding the plain inspector role. */
  PROVEN: [UserRole.PROVEN_INSPECTOR],

  /** Training and audit roles. */
  TRAINING: [UserRole.TRAINEE_INSPECTOR, UserRole.AUDITOR],

  /**
   * Company-wide administrator. Sees the whole company like COMPANY_ROOT, may create
   * other Company Admins, but ranks below Root and has no deletion immunity.
   * Always paired with {@link LocationScope.ALL}.
   */
  COMPANY_ADMIN: [UserRole.COMPANY_ADMIN],

  /** Company management: the roles that may administer other users. */
  MANAGEMENT: [UserRole.COMPANY_ROOT, UserRole.COMPANY_LOCATION_MANAGER],

  /** Rank-and-file company users. */
  STAFF: [UserRole.COMPANY_STANDARD_USER, UserRole.COMPANY_ADVANCED_USER],

  /** Company users with view-only access. */
  READ_ONLY: [UserRole.COMPANY_READ_ONLY_USER],

  /** Company users who coordinate inspectors. */
  COORDINATION: [UserRole.COMPANY_INSPECTOR_COORDINATOR],

  /** Customs-facing company users. */
  CUSTOMS: [UserRole.COMPANY_CUSTOMS_USER],

  /** Flooring-specific roles. */
  FLOORING: [
    UserRole.FLOORING_DEALERSHIP_MANAGER,
    UserRole.FLOORING_FINANCE_MANAGER,
    UserRole.FLOORING_MANAGER,
    UserRole.FLOORING_AUDITOR,
  ],
} as const;

/**
 * Role groups used to express access on a controller.
 *
 * Usage: `roles: [...Roles.ADMIN, ...Roles.MANAGEMENT, ...Roles.STAFF]`
 */
export const Roles = {
  ...ATOMIC_ROLES,

  /**
   * Every role that administers users inside a company: Company Admin plus Root and
   * Location Manager. Controllers gated on "company management level" should use this
   * rather than {@link Roles.MANAGEMENT}, which predates Company Admin.
   */
  COMPANY_MANAGEMENT: [...ATOMIC_ROLES.COMPANY_ADMIN, ...ATOMIC_ROLES.MANAGEMENT],

  /**
   * The roles whose breadth is the whole company by definition, and the only ones that may
   * change another user's location access.
   *
   * Distinct from {@link Roles.COMPANY_MANAGEMENT}, which also admits Location Manager. The
   * difference matters because breadth is its own axis: a Location Manager may hold
   * `LocationScope.ALL` and therefore resolve as company-wide, but it is still a Location
   * Manager — it may not hand out access. Gating that decision on resolved breadth alone
   * would let a promotion in *where* silently become a promotion in *what*.
   */
  COMPANY_WIDE: [UserRole.COMPANY_ROOT, ...ATOMIC_ROLES.COMPANY_ADMIN],

  /** Leasing-specific company roles: view-only plus inspector coordination. */
  LEASING: [...ATOMIC_ROLES.READ_ONLY, ...ATOMIC_ROLES.COORDINATION],

  /** Everyone who performs inspector work: admins, inspectors, trainees, auditors. */
  INSPECTOR_WORK: [...ATOMIC_ROLES.ADMIN, ...ATOMIC_ROLES.INSPECTORS, ...ATOMIC_ROLES.TRAINING],

  /** Every company-scoped role: admin, management, staff and the leasing roles. */
  ALL_COMPANY: [
    ...ATOMIC_ROLES.COMPANY_ADMIN,
    ...ATOMIC_ROLES.MANAGEMENT,
    ...ATOMIC_ROLES.STAFF,
    ...ATOMIC_ROLES.READ_ONLY,
    ...ATOMIC_ROLES.COORDINATION,
  ],

  /**
   * Broad platform access.
   *
   * NOTE: this deliberately does **not** include COMPANY_CUSTOMS_USER, which predates
   * the group and was never added. Do not read the name as "literally every role" —
   * a controller that must admit customs users has to spread {@link Roles.CUSTOMS} too.
   */
  ANY_USER: [
    ...ATOMIC_ROLES.ADMIN,
    ...ATOMIC_ROLES.COMPANY_ADMIN,
    ...ATOMIC_ROLES.MANAGEMENT,
    ...ATOMIC_ROLES.STAFF,
    ...ATOMIC_ROLES.READ_ONLY,
    ...ATOMIC_ROLES.COORDINATION,
    ...ATOMIC_ROLES.INSPECTORS,
    ...ATOMIC_ROLES.TRAINING,
    ...ATOMIC_ROLES.FLOORING,
  ],
} as const;

const ATOMIC_TENANTS = {
  /**
   * The operator's own organisation. A principal of this type can only hold
   * SUPER_ADMIN or an inspector role — the role configs define no company roles
   * for it — so tenancy never gates internal staff; the role list does.
   * `AccessGuard` admits this type on every endpoint implicitly.
   */
  INTERNAL: [CompanyType.SELF_INSPECTION],

  /** Client tenants on the standard operational flow, flooring excluded. */
  CLIENT: [CompanyType.DEALER, CompanyType.RENTAL, CompanyType.LEASING],
} as const;

/**
 * Company-type groups. Pass these to `AccessGuard`'s `clientTypes`, which lists
 * only the *client* tenants: `Tenants.INTERNAL` is always admitted, so omitting
 * `clientTypes` means "internal staff only".
 */
export const Tenants = {
  ...ATOMIC_TENANTS,

  /** Every client tenant, flooring included. */
  ALL_CLIENT: [...ATOMIC_TENANTS.CLIENT, CompanyType.FLOORING],

  /** Internal plus the standard operational client tenants (no flooring). */
  OPERATIONAL: [...ATOMIC_TENANTS.INTERNAL, ...ATOMIC_TENANTS.CLIENT],

  /** Every company type the platform supports. */
  ALL: [...ATOMIC_TENANTS.INTERNAL, ...ATOMIC_TENANTS.CLIENT, CompanyType.FLOORING],
} as const;

/**
 * Roles allowed to restore soft-deleted damages on frozen (estimate-request)
 * or COMPLETED inspections. A domain policy rather than an access group, so it
 * is kept out of {@link Roles}. Product may extend this list.
 * @constant
 */
export const DAMAGE_RESTORE_OVERRIDE_ROLES = [UserRole.SUPER_ADMIN] as const;

/* -------------------------------------------------------------------------- */
/* Deprecated flat aliases.                                                    */
/*                                                                             */
/* Kept so services that have not migrated keep compiling. Each is a reference */
/* to the group above, not a copy, so there is a single source of truth.       */
/* Delete an alias once no service references it.                              */
/* -------------------------------------------------------------------------- */

/** @deprecated Use `Roles.ADMIN`. */
export const ADMIN_ROLES = Roles.ADMIN;

/** @deprecated Use `Roles.INSPECTORS`. */
export const INSPECTOR_ROLES = Roles.INSPECTORS;

/** @deprecated Use `Roles.PROVEN`. */
export const PROVEN_INSPECTOR_ROLES = Roles.PROVEN;

/** @deprecated Use `Roles.TRAINING`. */
export const TRAINING_AND_AUDIT_ROLES = Roles.TRAINING;

/** @deprecated Use `Roles.MANAGEMENT`. */
export const COMPANY_MANAGEMENT_ROLES = Roles.MANAGEMENT;

/** @deprecated Use `Roles.STAFF`. */
export const COMPANY_OPERATIONAL_ROLES = Roles.STAFF;

/** @deprecated Use `Roles.READ_ONLY`. */
export const COMPANY_READ_ONLY_ROLES = Roles.READ_ONLY;

/** @deprecated Use `Roles.COORDINATION`. */
export const COMPANY_COORDINATION_ROLES = Roles.COORDINATION;

/** @deprecated Use `Roles.CUSTOMS`. */
export const COMPANY_CUSTOMS_ROLES = Roles.CUSTOMS;

/** @deprecated Use `Roles.LEASING`. */
export const COMPANY_LEASING_ROLES = Roles.LEASING;

/** @deprecated Use `Roles.FLOORING`. */
export const FLOORING_ROLES = Roles.FLOORING;

/** @deprecated Use `Roles.INSPECTOR_WORK`. */
export const INSPECTOR_WORK_ROLES = Roles.INSPECTOR_WORK;

/** @deprecated Use `Roles.ALL_COMPANY`. */
export const OPERATIONAL_USER_ROLES = Roles.ALL_COMPANY;

/** @deprecated Use `Roles.ANY_USER`. */
export const USERS_COMMON_ROLES = Roles.ANY_USER;

/** @deprecated Use `Tenants.INTERNAL`. */
export const INTERNAL_COMPANY_TYPES = Tenants.INTERNAL;

/** @deprecated Use `Tenants.CLIENT`. */
export const CLIENT_COMPANY_TYPES = Tenants.CLIENT;

/** @deprecated Use `Tenants.ALL_CLIENT`. */
export const ALL_CLIENT_COMPANY_TYPES = Tenants.ALL_CLIENT;

/** @deprecated Use `Tenants.OPERATIONAL`. */
export const DEFAULT_OPERATIONAL_COMPANY_TYPES = Tenants.OPERATIONAL;

/** @deprecated Use `Tenants.ALL`. */
export const ALL_SUPPORTED_COMPANY_TYPES = Tenants.ALL;
